import React, { useEffect, useState, useCallback } from "react";
import { API_BASE_URL } from "../config";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import Swal from "sweetalert2";
import "./BookedAppointment.css";
import Navbar from "../components/Navbar";
import { useLoading } from "../contexts/LoadingContext";
import Loader from "../components/loader";

const BookedAppointment = () => {
  const { isLoading, setIsLoading } = useLoading();
  const [view, setView] = useState("upcoming");
  const [appointments, setAppointments] = useState([]);
  const [session, setSession] = useState();
  const [message, setMessage] = useState("");
  const navigate = useNavigate();

  // Function to fetch appointments from the API
  const fetchAppointments = async (type) => {
    const patientId = session?.payLoad.user.Id; // Use session state here
    setIsLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/slots/booked-appointment?patientId=${patientId}&page=1&perPage=100&type=${type}`
      );
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await response.json();

      if (data.statusCode === 1) {
        setAppointments(data.payLoad);
        setMessage("");
      } else {
        setAppointments([]);
        setMessage(data.statusMessage[0]);
      }
    } catch (error) {
      console.error("Failed to fetch appointments:", error);
      setMessage("An error occurred while fetching appointments.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const sessionData = JSON.parse(sessionStorage.getItem("user"));

    if (!sessionData?.payLoad.access_token) {
      Swal.fire({
        icon: "info",
        title: "Login",
        text: "Please login first",
        confirmButtonText: "OK",
        allowOutsideClick: false,
      }).then(() => {
        navigate(`/login/bookedappointment`);
      });
    } else {
      setSession(sessionData);
    }
  }, [navigate]);

  useEffect(() => {
    if (session) {
      fetchAppointments(view);
    }
  }, [view, session]);

  const onDoctorProfile = useCallback(
    (id) => {
      navigate(`/doctor-profile/${id}`);
    },
    [navigate]
  );

  // Countdown timer state
  const [timers, setTimers] = useState([]);

  // Function to calculate remaining time
  const calculateRemainingTime = (appointmentDate, appointmentTime) => {
    const appointmentDateTime = new Date(
      `${appointmentDate} ${appointmentTime}`
    );
    const now = new Date();
    return Math.max(0, appointmentDateTime - now);
  };

  // Set up timers for upcoming appointments
  useEffect(() => {
    const newTimers = appointments.map((appointment) => ({
      id: appointment.Id,
      remainingTime: calculateRemainingTime(
        appointment.AppointmentDate,
        appointment.AppointmentTime
      ),
    }));

    setTimers(newTimers);

    const intervalId = setInterval(() => {
      setTimers((prevTimers) => {
        return prevTimers.map((timer) => {
          const remainingTime = calculateRemainingTime(
            appointments.find((a) => a.Id === timer.id).AppointmentDate,
            appointments.find((a) => a.Id === timer.id).AppointmentTime
          );
          return { ...timer, remainingTime };
        });
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [appointments]);
  const onAudioVideoCallClick = useCallback(
    (appointment, email, doctorId) => {
      if (!session?.payLoad.access_token) {
        Swal.fire({
          icon: "info",
          title: "Login",
          text: "Please login first",
          confirmButtonText: "OK",
          allowOutsideClick: false,
        }).then(() => {
          navigate(`/login/bookedappointment`);
          return;
        });
      } else {
        if (email) {
          var fromCall = "fromAppointment";
          navigate(`/video-call`, {
            state: { appointment, email, doctorId, fromCall },
          });
        } else {
          console.error("Doctor profile data is not available.");
        }
      }
    },
    [navigate, session]
  );

  const cancelAppointment = async (appointmentId) => {
    // console.log(appointmentId)
    const patientId = session?.payLoad?.user?.Id;

    if (!patientId || !appointmentId) {
      console.warn("Missing patientId or appointmentId");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/slots/cancel`, {
        method: "POST",
        body: JSON.stringify({
          patientId: patientId,
          appointmentId: appointmentId,
        }),
      });

      const data = await response.json();
      console.log("Cancel response:", data);

      if (data.statusCode === 1) {
        const updatedAppointments = Array.isArray(data.payLoad)
          ? data.payLoad
          : [];

        setAppointments(updatedAppointments);

        await Swal.fire({
          icon: "success",
          title: "Appointment Cancelled",
          text: "Your appointment has been successfully cancelled.",
          confirmButtonText: "OK",
          allowOutsideClick: false,
        });

        fetchAppointments(view);
        // setMessage("");
      } else {
        // setAppointments([]);
        setMessage(data.statusMessage?.[0] || "Cancellation failed.");
      }
    } catch (error) {
      console.error("Cancel appointment error:", error);
      setMessage("An error occurred while cancelling the appointment.");
    } finally {
      setIsLoading(false);
    }
  };
  const handleViewPrescription = useCallback(
    (consultationId) => {
      if (!consultationId) {
        Swal.fire({
          icon: "warning",
          title: "No Prescription",
          text: "No prescription available for this appointment.",
          confirmButtonText: "OK",
        });
        return;
      }

      // Navigate to prescription page with consultationId
      navigate(`/prescription/${consultationId}`);
    },
    [navigate]
  );
  const [copiedCodes, setCopiedCodes] = useState(new Set());

  const copyFollowUpCode = useCallback((followUpCode) => {
    if (!followUpCode) {
      return;
    }

    // Copy follow-up code to clipboard
    navigator.clipboard
      .writeText(followUpCode)
      .then(() => {
        setCopiedCodes((prev) => new Set([...prev, followUpCode]));
        // Reset icon after 2 seconds
        setTimeout(() => {
          setCopiedCodes((prev) => {
            const newSet = new Set(prev);
            newSet.delete(followUpCode);
            return newSet;
          });
        }, 2000);
      })
      .catch(() => {
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = followUpCode;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);

        setCopiedCodes((prev) => new Set([...prev, followUpCode]));
        // Reset icon after 2 seconds
        setTimeout(() => {
          setCopiedCodes((prev) => {
            const newSet = new Set(prev);
            newSet.delete(followUpCode);
            return newSet;
          });
        }, 2000);
      });
  }, []);

  return (
    <>
      <Navbar />
      <div className="container mt-4">
        {/* <div className="mb-4">
          <h2>Your Appointments</h2>
          <div className="btn-group">
            <button
              className={`btn ${
                view === "upcoming" ? "btn-primary" : "btn-outline-primary"
              }`}
              onClick={() => setView("upcoming")}
            >
              Upcoming
            </button>
            <button
              className={`btn ${
                view === "past" ? "btn-primary" : "btn-outline-primary"
              }`}
              onClick={() => setView("past")}
            >
              Past
            </button>
          </div>
        </div> */}

        <div className="row mb-3">
          <div className="col-md-8">
            <h4>
              {view === "upcoming"
                ? "Upcoming Appointments"
                : "Previous Appointments"}
            </h4>
          </div>
          <div className="col-md-4 text-end">
            <button
              className="btn btn-booked btn-primary"
              onClick={() => setView(view === "upcoming" ? "past" : "upcoming")}
            >
              {view === "upcoming"
                ? "View Previous Appointments"
                : "View Upcoming Appointments"}
            </button>
          </div>
        </div>
        {isLoading ? (
          <Loader type="prescription" count={3} />
        ) : appointments.length > 0 ? (
          <Row>
            {appointments.map((appointment) => {
              const timer = timers.find((t) => t.id === appointment.Id);
              const remainingTime = timer ? timer.remainingTime : 0;

              const seconds = Math.floor((remainingTime / 1000) % 60);
              const minutes = Math.floor((remainingTime / (1000 * 60)) % 60);
              const hours = Math.floor((remainingTime / (1000 * 60 * 60)) % 24);
              const days = Math.floor(remainingTime / (1000 * 60 * 60 * 24));

              const showJoinButton = remainingTime <= 0;

              return (
                <Col
                  key={appointment.Id}
                  xs={12}
                  md={6}
                  lg={4}
                  className="mb-3"
                >
                  {" "}
                  <div className="card py-1 pe-2 col-card d-flex flex-row">
                    <div className="col-md-4 d-flex flex-column align-items-center justify-content-center">
                      {/* View Prescription button above doctor image for completed appointments */}
                      {appointment.Status === "Completed" &&
                        appointment.ConsultationId && (
                          <button
                            className="btn btn-sm btn-outline-primary mb-2"
                            onClick={() =>
                              handleViewPrescription(appointment.ConsultationId)
                            }
                          >
                            Prescription
                          </button>
                        )}
                      <img
                        src={appointment.ImgLink}
                        alt="Doctor"
                        className="img-fluid rounded-circle doctor-image-modal"
                        style={{ width: "100px", height: "100px" }}
                      />
                      <p className="doctor-modal-name text-center">
                        <strong>{appointment.FirstName}</strong>
                      </p>
                      <p className="doctor-modal-name text-center text-muted">
                        {appointment.Allqualifications}
                      </p>{" "}
                      <span
                        className="badge bg-secondary mb-1"
                        onClick={() =>
                          onDoctorProfile(appointment.ApplicationUserId)
                        }
                      >
                        See Profile
                      </span>
                    </div>
                    <div className="col-md-8">
                      <p className="doctor-modal p-3">
                        <h5>Consultation Info</h5>
                        <strong>Selected Date</strong>
                        <span className="text-muted float-end">
                          {appointment.AppointmentDate}
                        </span>
                        <br />
                        <strong>Time Slot</strong>
                        <span className="text-muted float-end">
                          {appointment.AppointmentTime}
                        </span>
                        <br />
                        <strong>Appointment No</strong>
                        <span className="text-muted float-end">
                          {appointment.AppointmentNo
                            ? appointment.AppointmentNo
                            : "N/A"}
                        </span>
                      </p>{" "}
                      <div className="doctor-modal p-3 mb-1">
                        <h5>Payment Info</h5>
                        <strong>Status</strong>
                        <span
                          className={`${
                            appointment.Status === "Booked"
                              ? "text-success"
                              : appointment.Status === "Completed"
                              ? "text-success"
                              : "text-danger"
                          } float-end`}
                        >
                          {appointment.Status}
                        </span>
                        <br /> {/* Follow Up Code in Payment Info section */}
                        {appointment.Status === "Completed" &&
                          appointment.FollowUpCode && (
                            <>
                              <strong>Follow Up Code</strong>
                              <div
                                className="d-flex align-items-center justify-content-between p-2 border rounded"
                                onClick={() =>
                                  copyFollowUpCode(appointment.FollowUpCode)
                                }
                                title="Click to copy follow-up code"
                                style={{
                                  cursor: "pointer",
                                  backgroundColor: "#f8f9fa",
                                  minHeight: "38px",
                                  marginBottom: "-20px",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: "0.9em",
                                    fontFamily: "monospace",
                                    color: "#495057",
                                  }}
                                >
                                  {appointment.FollowUpCode}
                                </span>

                                <span
                                  style={{
                                    fontSize: "1.0em",
                                    color: "#198754",
                                  }}
                                >
                                  {copiedCodes.has(appointment.FollowUpCode)
                                    ? "Copied ✓"
                                    : "Copy 📋"}
                                </span>
                              </div>
                              <br />
                            </>
                          )}
                        {remainingTime > 4 * 60 * 60 * 1000 &&
                        appointment.Status === "Booked" ? (
                          <button
                            className="btn-sm d-block ms-auto"
                            onClick={() => cancelAppointment(appointment.Id)}
                          >
                            Cancel Appointment
                          </button>
                        ) : null}
                      </div>
                      <p className="countdown">
                        {view === "upcoming" ? (
                          showJoinButton ? (
                            <button
                              className="btn reschedule-btn"
                              onClick={() => {
                                onAudioVideoCallClick(
                                  appointment.AppointmentNo,
                                  appointment.Email,
                                  appointment.ApplicationUserId
                                );
                              }}
                            >
                              Join Now <span className="arrow">→</span>
                            </button>
                          ) : (
                            <span>
                              {days}d {hours}h {minutes}m {seconds}s
                            </span>
                          )
                        ) : null}
                      </p>
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        ) : (
          <div className="text-center">
            <p>{message || "No appointments found."}</p>
          </div>
        )}
      </div>
    </>
  );
};

export default BookedAppointment;
